import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import {
  LayoutDashboard, BarChart3, Users, Database, FileSpreadsheet,
  LogOut, KeyRound, ChevronDown, Building2, Menu, X, ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === "Admin";
  const isHR = user?.role === "HR" || isAdmin;

  const items = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard", show: true },
    { to: "/analytics", icon: BarChart3, label: "Analytics", show: true },
    { to: "/users", icon: Users, label: "User Management", show: isAdmin },
    { to: "/masters", icon: Database, label: "Master Data", show: isAdmin },
    { to: "/reports", icon: FileSpreadsheet, label: "Reports & Backup", show: true },
  ].filter((i) => i.show);

  const initials = (user?.employee_name || "U")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 border-r border-border bg-card flex-col transition-transform ${
          open ? "translate-x-0 flex" : "-translate-x-full hidden lg:flex lg:translate-x-0"
        }`}
      >
        <div className="h-16 px-5 flex items-center gap-3 border-b border-border">
          <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-base">United Group</div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              RMS · FY 25–26
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {items.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-foreground/70 hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 mx-3 mb-4 rounded-xl bg-accent/40 border border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="uppercase tracking-widest">Signed in</span>
          </div>
          <div className="text-sm font-medium truncate">{user?.employee_name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <div className="mt-2 inline-flex px-2 py-0.5 rounded-full text-[11px] bg-primary/10 text-primary font-medium">
            {user?.role} · {user?.entity}
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 px-4 sm:px-6 lg:px-8 border-b border-border bg-card flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 rounded-md hover:bg-accent"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">United Group</div>
              <div className="font-heading font-semibold text-base sm:text-lg">Recruitment Management</div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu-trigger"
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-full hover:bg-accent transition-colors"
              >
                <Avatar className="w-8 h-8 border border-border">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-medium">{user?.employee_name}</div>
                  <div className="text-[11px] text-muted-foreground">{user?.role}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-medium">{user?.employee_name}</div>
                <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="change-password-menu-item"
                onClick={() => nav("/profile")}
              >
                <KeyRound className="w-4 h-4 mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-menu-item"
                onClick={() => { logout(); nav("/login"); }}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8 animate-fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
